import React, {useState} from "react";
import {Badge, Button} from "react-bootstrap";
import {useMutation} from "react-query";
import {DropzoneFile, FileUpload} from "Components/FileUpload/FileUpload";
import {updateInscriptionLegalDocsConsent} from "Api/Mutation/InscriptionMutation";
import {Notificator} from "Services/Notificator/Notificator";

interface Props {
    inscription: any
    onUpdated: () => void
}

interface FormErrors {
    legalDocsConsent: string
    legalDocsFiles: string
}

const initFormErrors: FormErrors = {
    legalDocsConsent: '',
    legalDocsFiles: ''
}

export const InscriptionLegalDocsConsent: React.FC<Props> = (props) => {

    const [legalDocsConsent, setLegalDocsConsent] = useState<'granted' | 'denied' | null>(null)
    const [legalDocsFiles, setLegalDocsFiles] = useState<DropzoneFile[]>([])
    const [formErrors, setFormErrors] = useState<FormErrors>(initFormErrors)

    const updateLegalDocsConsentMutation = useMutation({
        mutationKey: ['updateInscriptionLegalDocsConsent', props.inscription.id],
        mutationFn: () => updateInscriptionLegalDocsConsent(
            props.inscription.id,
            legalDocsConsent ?? '',
            legalDocsConsent === 'denied' ? legalDocsFiles : []
        ),
        onSuccess: () => {
            Notificator.success('Se ha guardado la documentación correctamente.')
            props.onUpdated()
        },
        onError: () => {
            Notificator.error('Se ha producido un error al guardar la documentación.')
        }
    })

    const onLegalDocsConsentChange = (value: 'granted' | 'denied') => {
        setLegalDocsConsent(value)

        if (value === 'granted') {
            setLegalDocsFiles([])
        }
    }

    const validate = (): boolean => {
        let errorsList = {...initFormErrors}
        let errors = 0

        if (!legalDocsConsent) {
            errorsList = {
                ...errorsList,
                legalDocsConsent: 'Tienes que elegir una de las dos opciones sobre la consulta de bases de datos.'
            }
            errors++
        }

        if (legalDocsConsent === 'denied' && legalDocsFiles.length === 0) {
            errorsList = {
                ...errorsList,
                legalDocsFiles: 'Debes adjuntar al menos un archivo con la documentación solicitada.'
            }
            errors++
        }

        setFormErrors(errorsList)

        return errors === 0
    }

    const submit = () => {
        if (!validate()) {
            return
        }

        updateLegalDocsConsentMutation.mutate()
    }

    if (props.inscription.legalDocsConsent === 'granted' || props.inscription.legalDocsConsent === 'denied') {
        return (
            <div className={"mt-2"}>
                <strong>Consulta de bases de datos:</strong> &nbsp;
                {
                    props.inscription.legalDocsConsent === 'granted' &&
                    <Badge bg={"success"}>Autorizado</Badge>
                }
                {
                    props.inscription.legalDocsConsent === 'denied' &&
                    <Badge bg={"danger"}>No autorizado, aporta documentación</Badge>
                }

                {
                    props.inscription.legalDocsFiles?.length > 0 &&
                    <ul className={"mt-1"}>
                        {
                            props.inscription.legalDocsFiles.map((file: any, key: number) => (
                                <li key={key}>
                                    <a href={file.url} target={"_blank"}>{file.name}</a>
                                </li>
                            ))
                        }
                    </ul>
                }
            </div>
        )
    }

    return (
        <div className={"mt-2"}>
            <p className={formErrors.legalDocsConsent ? "text-danger mb-2" : "mb-2"}>
                Para poder seguir participando en las actividades de Sanse Concilia, es imprescindible
                que se elija entre las dos opciones que aparecen a continuación:
            </p>

            <p className={"ms-3"}>
                <label>
                    <input
                        type={"radio"}
                        name={"legalDocsConsent_" + props.inscription.id}
                        checked={legalDocsConsent === 'granted'}
                        onChange={() => onLegalDocsConsentChange('granted')}
                    />
                    &nbsp;&nbsp;Doy mi permiso para que sean consultadas las bases de datos
                    correspondientes (Hacienda, SEPE, Certificado de Discapacidad o Carnet y grado, Carnet
                    o reconocimiento de familia numerosa…) para poder formalizar la inscripción.
                </label>
            </p>

            <p className={"ms-3"}>
                <label>
                    <input
                        type={"radio"}
                        name={"legalDocsConsent_" + props.inscription.id}
                        checked={legalDocsConsent === 'denied'}
                        onChange={() => onLegalDocsConsentChange('denied')}
                    />
                    &nbsp;&nbsp;No doy mi permiso para que sean consultadas las bases de datos
                    correspondientes, por lo que adjunto escaneada la documentación necesaria solicitada
                    (Hacienda del último año, Certificado de Discapacidad o Carnet y grado, y para poder ser
                    beneficiario/a de los descuentos Carnet o reconocimiento de familia numerosa, situación
                    de desempleo actualizada –SEPE–, …) para poder formalizar la inscripción.
                </label>
            </p>

            {formErrors.legalDocsConsent && <p className={"text-danger"}>{formErrors.legalDocsConsent}</p>}

            {
                legalDocsConsent === 'denied' &&
                <div className={"mb-3 ms-3"}>
                    <FileUpload
                        onChange={setLegalDocsFiles}
                        defaultFiles={legalDocsFiles}
                        hasError={!!formErrors.legalDocsFiles}
                        acceptedMimmeTypes={"image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"}
                        allowedFilesText={"* Archivos permitidos: imágenes, PDF, Word (DOC/DOCX) y TXT."}
                    />
                    {formErrors.legalDocsFiles && <p className={"text-danger pt-2"}>{formErrors.legalDocsFiles}</p>}
                </div>
            }

            <Button
                className={"purple-btn"}
                onClick={submit}
                disabled={updateLegalDocsConsentMutation.isLoading}
            >Guardar</Button>
        </div>
    )
}
